import { range } from 'lodash';
import {
  VirtualizedTableColumnFit,
  VirtualizedTableColumnProps,
  VirtualizedTableRowAction,
  VirtualizedTableSelectionType,
} from './VirtualizedTable.interface';
import { Skeleton, SkeletonItem } from '@fluentui/react-components';
import React, { CSSProperties, useMemo } from 'react';
import { calculateGridTemplateColumns } from './VirtualizedTable.utils';
import classNames from 'classnames';

export interface VirtualizedTableLoadingSkeletonProps {
  actions?: VirtualizedTableRowAction[];
  selectionType: VirtualizedTableSelectionType;
  columnFit: VirtualizedTableColumnFit;
  hasRowIcons: boolean;
  style: CSSProperties;
  className?: string;
  columns: VirtualizedTableColumnProps[];
  rowIndex: number;
}

/**
 * Placeholder for a single row the table has no data for yet. One instance per
 * unloaded row index, so the caller's `rowsTotalCount` decides how many appear.
 */
export const VirtualizedTableLoadingSkeleton: React.FC<
  VirtualizedTableLoadingSkeletonProps
> = ({
  actions,
  selectionType,
  columnFit,
  hasRowIcons,
  style,
  className,
  columns,
  rowIndex,
}) => {
  const gridTemplateColumns: string = useMemo(() => {
    return calculateGridTemplateColumns(
      selectionType !== VirtualizedTableSelectionType.None,
      hasRowIcons,
      columnFit === VirtualizedTableColumnFit.Fit,
      actions !== undefined,
      columns,
    );
  }, [selectionType, hasRowIcons, columnFit, actions, columns]);

  return (
    <div
      role="row"
      aria-rowindex={rowIndex + 1}
      aria-busy={true}
      style={{
        ...style,
        display: 'grid',
        gridTemplateColumns: gridTemplateColumns,
      }}
      className={classNames(className, 'row')}
      tabIndex={-1}
    >
      {selectionType !== VirtualizedTableSelectionType.None && (
        <div role="cell" className="checkboxCol cell">
          <Skeleton>
            <SkeletonItem />
          </Skeleton>
        </div>
      )}
      {hasRowIcons && (
        <div role="cell" className="checkboxCol cell">
          <Skeleton>
            <SkeletonItem />
          </Skeleton>
        </div>
      )}
      {range(0, columns.length).map(columnIndex => (
        <div key={columnIndex} role="cell" className="cell">
          <Skeleton>
            <SkeletonItem />
          </Skeleton>
        </div>
      ))}
      {actions !== undefined && (
        <div
          role="cell"
          className="actionsCol cell"
          style={{ width: `${actions.length * 32 + 22}px` }}
        >
          <Skeleton>
            <SkeletonItem />
          </Skeleton>
        </div>
      )}
    </div>
  );
};
